import { apiService } from "@/services";
import { useMutation, useQuery } from "@tanstack/react-query";
import { handleApiError } from "@/utils";
import queryClient from "@/query-client";
import { DEFAULT_STALE_TIME } from "@/constants";

export interface BrandSubscription {
  brand_id: number;
  brand_name: string;
  subscribed_at: string;
}

export interface BrandSubscriptionsResponse {
  subscriptions: BrandSubscription[];
  count: number;
}

export interface AvailableBrand {
  id: number;
  name: string;
}

interface AvailableBrandsResponse {
  brands: AvailableBrand[];
}

interface UnsubscribeAllResponse {
  message: string;
  removed: number;
}

interface UnsubscribeResponse {
  message: string;
}

const BRAND_SUBSCRIPTIONS_KEY = ["brand-subscriptions"];
const BRAND_SUBSCRIPTIONS_AVAILABLE_KEY = ["brand-subscriptions", "available"];

export const useGetBrandSubscriptions = () =>
  useQuery<BrandSubscriptionsResponse>({
    queryKey: BRAND_SUBSCRIPTIONS_KEY,
    queryFn: () => apiService.get<BrandSubscriptionsResponse>("/brand-subscriptions"),
    staleTime: DEFAULT_STALE_TIME,
  });

export const useGetAvailableBrandsForSubscription = (enabled: boolean) =>
  useQuery<AvailableBrandsResponse>({
    queryKey: BRAND_SUBSCRIPTIONS_AVAILABLE_KEY,
    queryFn: () => apiService.get<AvailableBrandsResponse>("/brand-subscriptions/available"),
    staleTime: DEFAULT_STALE_TIME,
    enabled,
  });

export const useSubscribeBrand = () =>
  useMutation<BrandSubscription, unknown, { brand_id: number }>({
    mutationFn: (body) =>
      apiService.post<BrandSubscription, { brand_id: number }>("/brand-subscriptions", body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: BRAND_SUBSCRIPTIONS_KEY });
      queryClient.invalidateQueries({ queryKey: BRAND_SUBSCRIPTIONS_AVAILABLE_KEY });
    },
    onError: (error) => handleApiError(error),
  });

export const useUnsubscribeBrand = () =>
  useMutation<UnsubscribeResponse, unknown, number>({
    mutationFn: (brandId) =>
      apiService.delete<UnsubscribeResponse, undefined>(`/brand-subscriptions/${brandId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: BRAND_SUBSCRIPTIONS_KEY });
      queryClient.invalidateQueries({ queryKey: BRAND_SUBSCRIPTIONS_AVAILABLE_KEY });
    },
    onError: (error) => handleApiError(error),
  });

export const useUnsubscribeAllBrands = () =>
  useMutation<UnsubscribeAllResponse, unknown, void>({
    mutationFn: () => apiService.delete<UnsubscribeAllResponse, undefined>("/brand-subscriptions"),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: BRAND_SUBSCRIPTIONS_KEY });
      queryClient.invalidateQueries({ queryKey: BRAND_SUBSCRIPTIONS_AVAILABLE_KEY });
    },
    onError: (error) => handleApiError(error),
  });
